'use client';

import {
    CALCULATOR_TEMPLATES,
    getLeadCaptureFields,
    SAMPLE_CALCULATORS,
    validateCalculatorConfig,
} from '@/utils/jobPricingCalculator';
import {
    ArrowPathIcon,
    ArrowUpTrayIcon,
    CalculatorIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    PhotoIcon,
    PlusIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { SwatchIcon } from '@heroicons/react/20/solid';
import { normalizeSvgFileForUpload } from '@/utils/normalizeSvgUpload';
import { useMemo, useRef, useState } from 'react';
import { API } from './utils/api';
import { useEditor } from './context/EditorContext/EditorContext';
import { useNotifications } from './context/NotificationContext/NotificationContext';
import { MDEditor } from './atoms/MDEditor';
import { MediaPanel } from './MediaPanel';
import ColorSchemeSelect from './ui/ColorSchemeSelect';
import AlignmentButtons from './ui/AlignmentButtons';
import AnimationControls from './ui/AnimationControls';
import FontAwesomeIconPicker from './ui/FontAwesomeIconPicker';
import VerticalSpacingRow from './ui/VerticalSpacingRow';
import {
    COMPONENT_COLOR_SCHEME_GROUPS,
    componentSchemeToSwatch,
} from './helpers/colorScheme';

const FIELD_TYPES = [
    ['quantity', 'Quantity stepper'],
    ['number', 'Number'],
    ['square-footage', 'Square footage'],
    ['linear-footage', 'Linear footage'],
    ['rooms', 'Rooms'],
    ['select', 'Dropdown'],
    ['radio', 'Radio group'],
    ['checkbox', 'Checkbox group'],
    ['boolean', 'Yes / no'],
    ['smart-range', 'Smart range'],
    ['text', 'Text'],
];

const optionFields = new Set(['select', 'radio', 'checkbox', 'smart-range']);
const LEAD_FIELD_TYPES = [
    ['text', 'Short text'],
    ['tel', 'Phone'],
    ['email', 'Email'],
    ['textarea', 'Long text'],
];
const LEAD_COPY_DEFAULTS = {
    'leadCapture.eyebrow': 'Next step',
    'leadCapture.title': "Let's talk about your estimate",
    'leadCapture.submitText': 'Request a follow-up',
    'leadCapture.successTitle': 'Request received',
    'leadCapture.description':
        "Share your contact details and we'll follow up about your project and estimate.",
    'leadCapture.successMessage':
        'Thanks! Your estimate request has been sent.',
};
const makeId = (label, index) =>
    `${
        String(label || 'field')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '') || 'field'
    }-${index + 1}`;

const TEMPLATE_PROMPT_SUGGESTIONS = {
    'general-service': [
        {
            label: 'Add window cleaning',
            prompt: 'Add window cleaning as another service and keep the existing questions.',
        },
        {
            label: 'Add pressure washing',
            prompt: 'Add pressure washing as another service and keep the existing questions.',
        },
        {
            label: 'Add conditional logic',
            prompt: 'Add conditional questions so customers only see the questions for their selected service.',
        },
    ],
    'window-cleaning': [
        {
            label: 'Add pressure washing',
            prompt: 'Add pressure washing as another service and keep all existing Window Cleaning questions.',
        },
        {
            label: 'Add gutter cleaning',
            prompt: 'Add gutter cleaning as another service and keep all existing Window Cleaning questions.',
        },
        {
            label: 'Use one service dropdown',
            prompt: 'Use one Service dropdown for Window Cleaning and the added service; keep both service question branches conditional on the selection.',
        },
    ],
    'house-washing': [
        {
            label: 'Add window cleaning',
            prompt: 'Add window cleaning as another service and keep all existing Pressure Washing questions.',
        },
        {
            label: 'Add roof washing',
            prompt: 'Add roof washing as another service and keep all existing House Washing questions.',
        },
        {
            label: 'Use a service checkbox',
            prompt: 'Use a checkbox so customers can select one or more pressure washing services and show the selected service questions in order.',
        },
    ],
    'driveway-washing': [
        {
            label: 'Add house washing',
            prompt: 'Add house washing as another service and keep all existing Driveway Washing questions.',
        },
        {
            label: 'Add roof washing',
            prompt: 'Add roof washing as another service and keep all existing Driveway Washing questions.',
        },
        {
            label: 'Use a service checkbox',
            prompt: 'Use a checkbox so customers can select one or more pressure washing services and show the selected service questions in order.',
        },
    ],
    'interior-painting': [
        {
            label: 'Add exterior painting',
            prompt: 'Add exterior painting as another service and keep all existing Interior Painting questions.',
        },
        {
            label: 'Add cabinet painting',
            prompt: 'Add cabinet painting as another service and keep all existing Interior Painting questions.',
        },
        {
            label: 'Add conditional logic',
            prompt: 'Add conditional questions so customers only see the questions for their selected painting service.',
        },
    ],
    landscaping: [
        {
            label: 'Add lawn care',
            prompt: 'Add lawn care as another service and keep all existing Landscaping questions.',
        },
        {
            label: 'Add irrigation',
            prompt: 'Add irrigation as another service and keep all existing Landscaping questions.',
        },
        {
            label: 'Add conditional logic',
            prompt: 'Add conditional questions so customers only see the questions for their selected landscaping service.',
        },
    ],
    plumbing: [
        {
            label: 'Add drain cleaning',
            prompt: 'Add drain cleaning as another service and keep all existing Plumbing questions.',
        },
        {
            label: 'Add water heater service',
            prompt: 'Add water heater service as another service and keep all existing Plumbing questions.',
        },
        {
            label: 'Add conditional logic',
            prompt: 'Add conditional questions so customers only see the questions for their selected plumbing service.',
        },
    ],
    hvac: [
        {
            label: 'Add AC repair',
            prompt: 'Add AC repair as another service and keep all existing HVAC questions.',
        },
        {
            label: 'Add heating repair',
            prompt: 'Add heating repair as another service and keep all existing HVAC questions.',
        },
        {
            label: 'Add conditional logic',
            prompt: 'Add conditional questions so customers only see the questions for their selected HVAC service.',
        },
    ],
    roofing: [
        {
            label: 'Add roof repair',
            prompt: 'Add roof repair as another service and keep all existing Roof Replacement questions.',
        },
        {
            label: 'Add gutter cleaning',
            prompt: 'Add gutter cleaning as another service and keep all existing Roofing questions.',
        },
        {
            label: 'Add conditional logic',
            prompt: 'Add conditional questions so customers only see the questions for their selected roofing service.',
        },
    ],
    renovation: [
        {
            label: 'Add basement renovation',
            prompt: 'Add basement renovation as another service and keep all existing Renovation questions.',
        },
        {
            label: 'Add exterior renovation',
            prompt: 'Add exterior renovation as another service and keep all existing Renovation questions.',
        },
        {
            label: 'Add conditional logic',
            prompt: 'Add conditional questions so customers only see the questions for their selected renovation service.',
        },
    ],
};

function EditorPill({
    id,
    title,
    description,
    children,
    order,
    expanded,
    onToggle,
    onDragStart,
    onDrop,
    tone = 'default',
}) {
    const toneClasses =
        tone === 'cta'
            ? 'border-primary-700 bg-primary-700'
            : tone === 'primary'
            ? 'border-primary-200 bg-primary-50'
            : tone === 'secondary'
            ? 'border-secondary-200 bg-secondary-50'
            : 'border-primary-200 bg-primary-50';
    return (
        <div
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => onDrop(id)}
            style={{ order }}
            className={`group overflow-hidden rounded-xl border ${toneClasses} ${
                tone === 'cta' ? 'text-white' : ''
            }`}
        >
            <div
                className={`flex items-center gap-2 p-3 ${
                    tone === 'cta'
                        ? ''
                        : tone === 'secondary'
                        ? 'hover:bg-secondary-100/70'
                        : 'hover:bg-primary-100/70'
                }`}
            >
                <button
                    type="button"
                    draggable
                    onDragStart={(event) => {
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', id);
                        onDragStart(id);
                    }}
                    onDragEnd={() => onDragStart(null)}
                    onClick={onToggle}
                    className="flex min-w-0 flex-1 cursor-grab items-center justify-between gap-3 rounded-lg text-left active:cursor-grabbing"
                >
                    <span className="min-w-0">
                        <span
                            className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] ${
                                tone === 'cta'
                                    ? 'text-white/80'
                                    : tone === 'secondary'
                                    ? 'text-secondary-700'
                                    : 'text-primary-700'
                            }`}
                        >
                            {title}
                        </span>
                        {description && (
                            <span
                                className={`mt-1 block truncate text-sm font-semibold normal-case tracking-normal ${
                                    tone === 'cta'
                                        ? 'text-white'
                                        : 'text-neutral-900'
                                }`}
                            >
                                {description}
                            </span>
                        )}
                    </span>
                    <ChevronDownIcon
                        className={`h-4 w-4 shrink-0 transition-transform ${
                            tone === 'cta'
                                ? 'text-white'
                                : tone === 'secondary'
                                ? 'text-secondary-700'
                                : 'text-primary-700'
                        } ${expanded ? 'rotate-180' : ''}`}
                    />
                </button>
            </div>
            {expanded && (
                <div
                    className={`border-t p-3 ${
                        tone === 'cta'
                            ? 'border-primary-500 bg-white'
                            : tone === 'primary'
                            ? 'border-primary-200'
                            : tone === 'secondary'
                            ? 'border-secondary-200'
                            : 'border-neutral-100'
                    }`}
                >
                    {children}
                </div>
            )}
        </div>
    );
}

function MediaPill({
    title,
    value,
    expanded,
    onToggle,
    onSelect,
    onRemove,
    children,
}) {
    const image = String(value || '').trim();
    const editor = useEditor();
    const notifications = useNotifications();
    const [isUploading, setIsUploading] = useState(false);
    const uploadInputRef = useRef(null);
    const selectedImage = editor.site?.data?.images?.find(
        (item) => item?.url === image
    );
    const imageName =
        selectedImage?.filename ||
        decodeURIComponent(
            image.split(/[?#]/)[0].split('/').pop() || 'Selected image'
        );
    const handleUpload = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || isUploading) return;

        setIsUploading(true);
        try {
            const companyId = editor.global?.data?.company_id;
            if (!companyId) throw new Error('Company ID is unavailable.');

            const normalizedImage = await normalizeSvgFileForUpload(file);
            const formData = new FormData();
            formData.append('image', normalizedImage);
            const uploadUrl =
                `https://image-search-ytnjkd5upa-uc.a.run.app/upload/image/${companyId}?` +
                new URLSearchParams({
                    filename: file.name.replace(/ +/g, '-').toLowerCase(),
                });
            const response = await fetch(uploadUrl, {
                method: 'POST',
                body: formData,
            });
            if (!response.ok) {
                throw new Error(`Image upload failed: ${response.statusText}`);
            }

            const data = await response.json();
            editor.site.addImage?.({
                ...data,
                size_bytes: file.size,
            });
            onSelect(data.url);
        } catch (error) {
            notifications.push({
                title: 'Image upload failed',
                variant: 'error',
                error,
            });
        } finally {
            setIsUploading(false);
        }
    };
    return (
        <div className="w-full min-w-0 max-w-full overflow-hidden rounded-xl border border-primary-200 bg-white shadow-sm">
            <div
                style={
                    image
                        ? {
                              backgroundImage: `linear-gradient(135deg, rgba(5, 12, 23, 0.78), rgba(5, 12, 23, 0.42)), url(${JSON.stringify(
                                  image
                              )})`,
                              backgroundPosition: 'center',
                              backgroundSize: 'cover',
                          }
                        : undefined
                }
                className={`flex w-full min-w-0 max-w-full items-center justify-between gap-3 text-left ${
                    image
                        ? 'px-3 py-2 text-white'
                        : 'px-3 py-2 text-primary-700 hover:bg-primary-100'
                }`}
            >
                <button
                    type="button"
                    onClick={onToggle}
                    className="flex min-w-0 flex-1 items-center text-left"
                >
                    <span className="flex min-w-0 flex-1 flex-col items-start gap-1 overflow-hidden">
                        {!image && <PhotoIcon className="h-4 w-4 shrink-0" />}
                        {image ? (
                            <>
                                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80">
                                    MEDIA 1
                                </span>
                                <span
                                    title={imageName}
                                    className="block w-full min-w-0 truncate text-sm font-semibold text-white"
                                >
                                    {imageName}
                                </span>
                            </>
                        ) : (
                            <span className="truncate text-xs font-semibold">
                                {title}
                            </span>
                        )}
                    </span>
                </button>
                <div className="flex shrink-0 items-center gap-1">
                    <button
                        type="button"
                        title="Upload a file"
                        aria-label={`Upload ${title.toLowerCase()}`}
                        disabled={isUploading}
                        onClick={() => uploadInputRef.current?.click()}
                        className={`inline-flex h-6 w-6 items-center justify-center rounded-full transition-all focus:outline-none focus-visible:ring-2 disabled:opacity-40 ${
                            image
                                ? 'bg-black/15 text-white shadow-sm backdrop-blur-[2px] hover:bg-black/30 focus-visible:ring-white/60'
                                : 'focus-visible:ring-primary-500/60 text-neutral-500 hover:bg-primary-100 hover:text-primary-700'
                        }`}
                    >
                        {isUploading ? (
                            <ArrowPathIcon
                                className="h-3 w-3 animate-spin"
                                aria-hidden="true"
                            />
                        ) : (
                            <ArrowUpTrayIcon
                                className="h-3 w-3"
                                aria-hidden="true"
                            />
                        )}
                    </button>
                    <input
                        ref={uploadInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={isUploading}
                        onChange={handleUpload}
                    />
                    <button
                        type="button"
                        onClick={onToggle}
                        aria-label={`${
                            expanded ? 'Collapse' : 'Expand'
                        } ${title.toLowerCase()}`}
                        className={`inline-flex h-6 w-6 items-center justify-center rounded-full transition-all focus:outline-none focus-visible:ring-2 ${
                            image
                                ? 'bg-black/15 text-white shadow-sm backdrop-blur-[2px] hover:bg-black/30 focus-visible:ring-white/60'
                                : 'focus-visible:ring-primary-500/60 text-primary-700 hover:bg-primary-100'
                        }`}
                    >
                        <ChevronDownIcon
                            className={`h-3 w-3 transition-transform ${
                                expanded ? 'rotate-180' : ''
                            }`}
                        />
                    </button>
                </div>
            </div>
            {expanded && (
                <div className="space-y-3 border-t border-primary-100 p-3">
                    {image && (
                        <img
                            src={image}
                            alt="Selected background"
                            className="h-24 w-full rounded-lg object-cover"
                        />
                    )}
                    <MediaPanel
                        inline
                        hidePreview
                        currentValue={image}
                        onSelectImage={onSelect}
                        onSelectCustom={onSelect}
                    />
                    <div className="flex gap-2">
                        {image && (
                            <button
                                type="button"
                                onClick={onRemove}
                                className="rounded-lg px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50"
                            >
                                Remove
                            </button>
                        )}
                    </div>
                    {children}
                </div>
            )}
        </div>
    );
}

function Toggle({ checked, onChange, ariaLabel, disabled = false }) {
    return (
        <label
            className={`relative inline-flex items-center ${
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            }`}
        >
            <input
                type="checkbox"
                className="peer sr-only"
                checked={checked}
                onChange={(event) => onChange(event.target.checked)}
                aria-label={ariaLabel}
                disabled={disabled}
            />
            <span className="peer h-5 w-9 rounded-full bg-neutral-300 after:absolute after:left-[3px] after:top-0.5 after:h-4 after:w-4 after:rounded-full after:border after:border-neutral-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-600 peer-checked:after:translate-x-4 peer-checked:after:border-white peer-focus:ring-1 peer-focus:ring-primary-300" />
        </label>
    );
}

function ActionInput({ label, value, onChange }) {
    return (
        <label className="block text-xs font-semibold leading-5 text-primary-700">
            {label}
            <div className="mt-1 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                <input
                    value={value || ''}
                    onChange={(event) => onChange(event.target.value)}
                    className="block h-9 w-full rounded-[10px] border-0 bg-white px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                />
            </div>
        </label>
    );
}

function ActionControls({
    label,
    text,
    link,
    icon,
    iconVisible,
    openInNewTab,
    onTextChange,
    onLinkChange,
    onIconChange,
    onIconVisibleChange,
    onOpenInNewTabChange,
}) {
    return (
        <>
            <ActionInput label={label} value={text} onChange={onTextChange} />
            <div className="mt-3">
                <label className="block text-xs font-semibold leading-5 text-primary-700">
                    {label} Icon
                </label>
                <div className="mt-2">
                    <FontAwesomeIconPicker
                        value={icon || ''}
                        onChange={onIconChange}
                    />
                </div>
            </div>
            <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-neutral-100 px-2 py-1">
                <span className="text-[11px] font-semibold leading-4 text-primary-700">
                    Show Icon
                </span>
                <Toggle
                    checked={iconVisible !== false}
                    onChange={onIconVisibleChange}
                    ariaLabel={`${label} icon visible`}
                />
            </div>
            <div className="mt-3">
                <ActionInput
                    label={`${label} Link`}
                    value={link}
                    onChange={onLinkChange}
                />
            </div>
            <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-neutral-100 px-2 py-1">
                <span className="text-[11px] font-semibold leading-4 text-primary-700">
                    Open in new tab
                </span>
                <Toggle
                    checked={Boolean(openInNewTab)}
                    onChange={onOpenInNewTabChange}
                    ariaLabel={`${label} open in new tab`}
                />
            </div>
        </>
    );
}

function VerticalAlignmentButtons({ value = 'center', onChange }) {
    const choices = [
        ['top', 'fa-objects-align-top', 'Vertical top'],
        ['center', 'fa-objects-align-center-vertical', 'Vertical center'],
        ['bottom', 'fa-objects-align-bottom', 'Vertical bottom'],
    ];
    return (
        <div className="inline-flex gap-2">
            {choices.map(([key, icon, label]) => {
                const active = value === key;
                return (
                    <span
                        key={key}
                        className={`relative inline-flex rounded-xl p-[2px] transition-colors duration-300 ease-out ${
                            active ? 'bg-primary-600' : 'bg-neutral-300'
                        }`}
                    >
                        <button
                            type="button"
                            onClick={() => onChange(key)}
                            aria-pressed={active}
                            aria-label={label}
                            className={`inline-flex h-9 w-12 items-center justify-center rounded-[10px] bg-white shadow-sm transition-[opacity,box-shadow] duration-300 ease-out hover:shadow focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-1 ${
                                active
                                    ? 'text-primary-600 ring-2 ring-primary-600 ring-offset-1 ring-offset-white'
                                    : 'text-neutral-500 ring-2 ring-neutral-200'
                            }`}
                        >
                            <i
                                className={`fa-solid ${icon} text-sm`}
                                aria-hidden="true"
                            />
                        </button>
                    </span>
                );
            })}
        </div>
    );
}

function QuestionDisplayButtons({ value = 'all', onChange }) {
    const choices = [
        ['all', 'fa-list', 'All questions in one column'],
        ['stepper', 'fa-forward-step', 'One question at a time'],
    ];
    return (
        <div className="inline-flex gap-2">
            {choices.map(([key, icon, label]) => {
                const active = value === key;
                return (
                    <span
                        key={key}
                        className={`relative inline-flex rounded-xl p-[2px] transition-colors duration-300 ease-out ${
                            active ? 'bg-primary-600' : 'bg-neutral-300'
                        }`}
                    >
                        <button
                            type="button"
                            title={label}
                            onClick={() => onChange(key)}
                            aria-pressed={active}
                            aria-label={label}
                            className={`inline-flex h-9 w-12 items-center justify-center rounded-[10px] bg-white shadow-sm transition-[opacity,box-shadow] duration-300 ease-out hover:shadow focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-1 ${
                                active
                                    ? 'text-primary-600 ring-2 ring-primary-600 ring-offset-1 ring-offset-white'
                                    : 'text-neutral-500 ring-2 ring-neutral-200'
                            }`}
                        >
                            <i
                                className={`fa-solid ${icon} text-sm`}
                                aria-hidden="true"
                            />
                        </button>
                    </span>
                );
            })}
        </div>
    );
}

const CONDITION_OPERATORS = [
    ['equals', 'is'],
    ['does-not-equal', 'is not'],
    ['contains', 'contains'],
    ['is-selected', 'includes'],
    ['has-value', 'has a value'],
    ['is-empty', 'is empty'],
    ['greater-than', 'is greater than'],
    ['less-than', 'is less than'],
    ['greater-than-or-equal', 'is at least'],
    ['less-than-or-equal', 'is at most'],
];

function operatorsForField(field) {
    if (field?.type === 'boolean')
        return CONDITION_OPERATORS.filter(([value]) =>
            ['equals', 'does-not-equal', 'has-value', 'is-empty'].includes(
                value
            )
        );
    if (
        [
            'quantity',
            'number',
            'square-footage',
            'linear-footage',
            'rooms',
        ].includes(field?.type)
    )
        return CONDITION_OPERATORS.filter(([value]) =>
            [
                'equals',
                'does-not-equal',
                'greater-than',
                'less-than',
                'greater-than-or-equal',
                'less-than-or-equal',
                'has-value',
                'is-empty',
            ].includes(value)
        );
    if (field?.type === 'checkbox')
        return CONDITION_OPERATORS.filter(([value]) =>
            [
                'equals',
                'does-not-equal',
                'contains',
                'is-selected',
                'has-value',
                'is-empty',
            ].includes(value)
        );
    return CONDITION_OPERATORS.filter(([value]) =>
        [
            'equals',
            'does-not-equal',
            'contains',
            'has-value',
            'is-empty',
        ].includes(value)
    );
}

function defaultConditionForField(field) {
    const operators = operatorsForField(field);
    const operator = operators[0]?.[0] || 'equals';
    let value = field?.options?.[0]?.value ?? '';
    if (field?.type === 'boolean') value = true;
    return { fieldId: field?.id || '', operator, value };
}

function ConditionValue({ field, condition, disabled, onChange }) {
    if (['has-value', 'is-empty'].includes(condition.operator)) return null;
    if (field?.type === 'boolean')
        return (
            <select
                value={String(Boolean(condition.value))}
                disabled={disabled}
                onChange={(event) => onChange(event.target.value === 'true')}
                className="min-w-0 flex-1 rounded-lg border border-neutral-200 bg-white px-2 py-2 text-xs font-normal text-neutral-800"
            >
                <option value="true">Yes</option>
                <option value="false">No</option>
            </select>
        );
    if (field?.options?.length)
        return (
            <select
                value={condition.value ?? ''}
                disabled={disabled}
                onChange={(event) => onChange(event.target.value)}
                className="min-w-0 flex-1 rounded-lg border border-neutral-200 bg-white px-2 py-2 text-xs font-normal text-neutral-800"
            >
                <option value="">Choose answer</option>
                {field.options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        );
    return (
        <input
            type={
                [
                    'quantity',
                    'number',
                    'square-footage',
                    'linear-footage',
                    'rooms',
                ].includes(field?.type)
                    ? 'number'
                    : 'text'
            }
            value={condition.value ?? ''}
            disabled={disabled}
            onChange={(event) =>
                onChange(
                    event.target.type === 'number' && event.target.value !== ''
                        ? Number(event.target.value)
                        : event.target.value
                )
            }
            placeholder="Value"
            className="min-w-0 flex-1 rounded-lg border border-neutral-200 px-2 py-2 text-xs font-normal text-neutral-800"
        />
    );
}

function ConditionGroupEditor({ group, fields, onChange, depth = 0 }) {
    const conditions = Array.isArray(group?.conditions) ? group.conditions : [];
    const fieldById = new Map(fields.map((field) => [field.id, field]));
    const updateConditions = (nextConditions) =>
        onChange({
            ...group,
            logic: group?.logic === 'or' ? 'or' : 'and',
            conditions: nextConditions,
        });
    const updateCondition = (index, patch) => {
        const next = conditions.map((condition, conditionIndex) =>
            conditionIndex === index ? { ...condition, ...patch } : condition
        );
        updateConditions(next);
    };
    const removeCondition = (index) =>
        updateConditions(
            conditions.filter((_, conditionIndex) => conditionIndex !== index)
        );
    const addCondition = () => {
        const firstField = fields[0];
        if (!firstField) return;
        updateConditions([...conditions, defaultConditionForField(firstField)]);
    };
    return (
        <div
            className={`space-y-3 rounded-xl p-3 ${
                depth === 0
                    ? 'bg-secondary-50'
                    : 'border border-secondary-200 bg-white'
            }`}
        >
            <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-semibold text-primary-700">
                    {depth === 0 ? 'Show this question when' : 'Nested group'}
                </span>
                <label className="flex items-center gap-2 text-[11px] font-semibold text-primary-700">
                    Match
                    <select
                        value={group?.logic === 'or' ? 'or' : 'and'}
                        onChange={(event) =>
                            onChange({
                                ...group,
                                logic: event.target.value,
                            })
                        }
                        className="rounded-lg border border-neutral-200 bg-white px-2 py-1.5 text-xs font-normal text-neutral-800"
                    >
                        <option value="and">All conditions</option>
                        <option value="or">Any condition</option>
                    </select>
                </label>
            </div>
            {conditions.map((condition, index) => {
                if (condition?.group) {
                    return (
                        <div key={`group-${index}`} className="space-y-2">
                            <ConditionGroupEditor
                                group={condition.group}
                                fields={fields}
                                depth={depth + 1}
                                onChange={(nextGroup) =>
                                    updateCondition(index, { group: nextGroup })
                                }
                            />
                            <button
                                type="button"
                                onClick={() => removeCondition(index)}
                                className="text-[11px] font-semibold text-red-600 hover:text-red-700"
                            >
                                Remove nested group
                            </button>
                        </div>
                    );
                }
                const selectedField = fieldById.get(condition?.fieldId);
                const availableOperators = operatorsForField(selectedField);
                return (
                    <div
                        key={`${condition?.fieldId || 'condition'}-${index}`}
                        className="flex flex-wrap items-center gap-2 rounded-lg border border-secondary-200 bg-white p-2"
                    >
                        <select
                            value={condition?.fieldId || ''}
                            onChange={(event) => {
                                const nextField = fieldById.get(
                                    event.target.value
                                );
                                updateCondition(
                                    index,
                                    defaultConditionForField(nextField)
                                );
                            }}
                            className="min-w-0 flex-1 rounded-lg border border-neutral-200 bg-white px-2 py-2 text-xs font-normal text-neutral-800"
                        >
                            {fields.map((field) => (
                                <option key={field.id} value={field.id}>
                                    {field.label || field.id}
                                </option>
                            ))}
                        </select>
                        <select
                            value={
                                availableOperators.some(
                                    ([value]) => value === condition?.operator
                                )
                                    ? condition.operator
                                    : availableOperators[0]?.[0] || 'equals'
                            }
                            onChange={(event) =>
                                updateCondition(index, {
                                    operator: event.target.value,
                                })
                            }
                            className="min-w-0 flex-1 rounded-lg border border-neutral-200 bg-white px-2 py-2 text-xs font-normal text-neutral-800"
                        >
                            {availableOperators.map(([value, label]) => (
                                <option key={value} value={value}>
                                    {label}
                                </option>
                            ))}
                        </select>
                        <ConditionValue
                            field={selectedField}
                            condition={condition}
                            onChange={(value) =>
                                updateCondition(index, { value })
                            }
                        />
                        <button
                            type="button"
                            aria-label="Remove condition"
                            onClick={() => removeCondition(index)}
                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-primary-700 hover:bg-red-50 hover:text-red-600"
                        >
                            <TrashIcon className="h-4 w-4" />
                        </button>
                    </div>
                );
            })}
            <button
                type="button"
                onClick={addCondition}
                disabled={!fields.length}
                className="inline-flex items-center gap-1 rounded-lg border border-primary-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-primary-700 hover:bg-primary-50 disabled:opacity-50"
            >
                <PlusIcon className="h-3.5 w-3.5" /> Add condition
            </button>
        </div>
    );
}

export default function JobPricingCalculatorEditor({
    componentProps,
    file,
    onChange,
}) {
    const config = componentProps?.CalculatorConfig?.fields
        ? componentProps.CalculatorConfig
        : SAMPLE_CALCULATORS.windowCleaning;
    const leadFields = useMemo(() => getLeadCaptureFields(config), [config]);
    const [templateId, setTemplateId] = useState('');
    const [expandedField, setExpandedField] = useState(null);
    const [draggedField, setDraggedField] = useState(null);
    const [expandedLeadField, setExpandedLeadField] = useState(null);
    const [draggedLeadField, setDraggedLeadField] = useState(null);
    const [pricingPrompt, setPricingPrompt] = useState('');
    const [generating, setGenerating] = useState(false);
    const [generationSummary, setGenerationSummary] = useState([]);
    const [openPills, setOpenPills] = useState({});
    const [pillOrder, setPillOrder] = useState(['pricing', 'results', 'ctas']);
    const [draggedPill, setDraggedPill] = useState(null);
    const [openMediaPill, setOpenMediaPill] = useState(null);
    const dragPreviewNodeRef = useRef(null);
    const suppressFieldDragClickRef = useRef(false);
    const suppressLeadFieldDragClickRef = useRef(false);
    const editor = useEditor();
    const notifications = useNotifications();
    const promptSuggestions = useMemo(
        () =>
            TEMPLATE_PROMPT_SUGGESTIONS[templateId] || [
                {
                    label: 'Add another service',
                    prompt: 'Add another service and keep all existing questions, pricing, and copy.',
                },
                {
                    label: 'Add conditional logic',
                    prompt: 'Add conditional questions so customers only see the questions for their selected service.',
                },
            ],
        [templateId]
    );
    const errors = useMemo(() => validateCalculatorConfig(config), [config]);
    const pillOrderValue = (id) => 6 + pillOrder.indexOf(id);
    const togglePill = (id) =>
        setOpenPills((current) => ({ ...current, [id]: !current[id] }));
    const movePill = (targetId) => {
        if (!draggedPill || draggedPill === targetId) return;
        setPillOrder((current) => {
            const next = current.filter((id) => id !== draggedPill);
            next.splice(next.indexOf(targetId), 0, draggedPill);
            return next;
        });
        setDraggedPill(null);
    };

    const save = (next) =>
        onChange({
            file,
            key: 'CalculatorConfig',
            value: structuredClone(next),
        });
    const updateComponentProp = (key, value) => onChange({ file, key, value });
    const update = (path, value) => {
        const next = structuredClone(config);
        let current = next;
        const keys = path.split('.');
        keys.slice(0, -1).forEach((key) => {
            current[key] ||= {};
            current = current[key];
        });
        current[keys.at(-1)] = value;
        save(next);
    };
    const updateLeadField = (index, patch) => {
        update(
            'leadCapture.fields',
            leadFields.map((field, fieldIndex) =>
                fieldIndex === index ? { ...field, ...patch } : field
            )
        );
    };
    const moveLeadField = (from, insertionIndex) => {
        if (insertionIndex < 0 || insertionIndex > leadFields.length) return;
        const next = [...leadFields];
        const [field] = next.splice(from, 1);
        if (!field) return;
        const targetIndex =
            from < insertionIndex ? insertionIndex - 1 : insertionIndex;
        if (from === targetIndex) return;
        next.splice(targetIndex, 0, field);
        update('leadCapture.fields', next);
        setExpandedLeadField((current) => {
            if (current === from) return targetIndex;
            if (from < current && targetIndex >= current) return current - 1;
            if (from > current && targetIndex <= current) return current + 1;
            return current;
        });
    };
    const startLeadFieldDrag = (event, fromIndex) => {
        const payload = {
            fromIndex,
            overIndex: fromIndex,
            overPosition: 'before',
        };
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('application/json', JSON.stringify(payload));
        event.dataTransfer.setData('text/plain', JSON.stringify(payload));
        const sourceCard = event.currentTarget.closest(
            '[data-draggable-lead-field="true"]'
        );
        if (sourceCard) {
            const rect = sourceCard.getBoundingClientRect();
            const previewNode = sourceCard.cloneNode(true);
            const offsetX = Math.max(
                0,
                Math.min(rect.width - 1, event.clientX - rect.left)
            );
            const offsetY = Math.max(
                0,
                Math.min(rect.height - 1, event.clientY - rect.top)
            );
            previewNode.style.position = 'fixed';
            previewNode.style.top = '-10000px';
            previewNode.style.left = '-10000px';
            previewNode.style.width = `${rect.width}px`;
            previewNode.style.maxWidth = `${rect.width}px`;
            previewNode.style.margin = '0';
            previewNode.style.pointerEvents = 'none';
            previewNode.style.borderRadius = '16px';
            previewNode.style.overflow = 'hidden';
            previewNode.style.boxShadow = '0 18px 36px rgba(15, 23, 42, 0.18)';
            previewNode.style.opacity = '0.98';
            previewNode.style.background = 'white';
            document.body.appendChild(previewNode);
            dragPreviewNodeRef.current = previewNode;
            event.dataTransfer.setDragImage(previewNode, offsetX, offsetY);
        }
        setDraggedLeadField(payload);
    };
    const dragLeadFieldOver = (event, overIndex) => {
        if (!draggedLeadField) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        const targetRect = event.currentTarget.getBoundingClientRect();
        const rawPosition =
            event.clientY - targetRect.top < targetRect.height / 2
                ? 'before'
                : 'after';
        const overPosition = normalizeFieldDropPosition(
            draggedLeadField.fromIndex,
            overIndex,
            rawPosition
        );
        setDraggedLeadField((current) => ({
            ...current,
            overIndex,
            overPosition,
        }));
    };
    const endLeadFieldDrag = () => {
        setDraggedLeadField(null);
        if (dragPreviewNodeRef.current) {
            dragPreviewNodeRef.current.remove();
            dragPreviewNodeRef.current = null;
        }
        suppressLeadFieldDragClickRef.current = true;
        window.setTimeout(() => {
            suppressLeadFieldDragClickRef.current = false;
        }, 0);
    };
    const dropLeadField = (event, overIndex) => {
        if (!draggedLeadField) return;
        event.preventDefault();
        const targetRect = event.currentTarget.getBoundingClientRect();
        const rawPosition =
            event.clientY - targetRect.top < targetRect.height / 2
                ? 'before'
                : 'after';
        const overPosition = normalizeFieldDropPosition(
            draggedLeadField.fromIndex,
            overIndex,
            rawPosition
        );
        moveLeadField(
            draggedLeadField.fromIndex,
            overPosition === 'after' ? overIndex + 1 : overIndex
        );
        endLeadFieldDrag();
    };
    const removeLeadField = (index) => {
        if (leadFields[index]?.id === 'email') return;
        update(
            'leadCapture.fields',
            leadFields.filter((_, fieldIndex) => fieldIndex !== index)
        );
        setExpandedLeadField(null);
    };
    const addLeadField = () => {
        const ids = new Set(leadFields.map((field) => field.id));
        let suffix = leadFields.length + 1;
        let id = `custom-field-${suffix}`;
        while (ids.has(id)) {
            suffix += 1;
            id = `custom-field-${suffix}`;
        }
        update('leadCapture.fields', [
            ...leadFields,
            {
                id,
                type: 'text',
                label: 'Additional information',
                placeholder: '',
                required: false,
            },
        ]);
        setExpandedLeadField(leadFields.length);
    };
    const updateField = (index, patch) => {
        const next = structuredClone(config);
        next.fields[index] = { ...next.fields[index], ...patch };
        if (patch.label && !next.fields[index].id) {
            next.fields[index].id = makeId(patch.label, index);
        }
        save(next);
    };
    const updateFieldVisibility = (index, visibility) => {
        const next = structuredClone(config);
        if (visibility?.conditions?.length) {
            next.fields[index].visibility = visibility;
        } else {
            delete next.fields[index].visibility;
        }
        save(next);
    };
    const moveField = (from, insertionIndex) => {
        if (insertionIndex < 0 || insertionIndex > config.fields.length) return;
        const next = structuredClone(config);
        const [field] = next.fields.splice(from, 1);
        if (!field) return;
        const targetIndex =
            from < insertionIndex ? insertionIndex - 1 : insertionIndex;
        if (from === targetIndex) return;
        next.fields.splice(targetIndex, 0, field);
        save(next);
    };
    const normalizeFieldDropPosition = (fromIndex, overIndex, position) => {
        if (position === 'before' && overIndex === fromIndex + 1)
            return 'after';
        if (position === 'after' && overIndex === fromIndex - 1)
            return 'before';
        return position;
    };
    const isNoOpFieldDrop = (fromIndex, overIndex, position) => {
        const insertionIndex = position === 'after' ? overIndex + 1 : overIndex;
        const targetIndex =
            fromIndex < insertionIndex ? insertionIndex - 1 : insertionIndex;
        return fromIndex === targetIndex;
    };
    const startFieldDrag = (event, fromIndex) => {
        const payload = {
            fromIndex,
            overIndex: fromIndex,
            overPosition: 'before',
        };
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('application/json', JSON.stringify(payload));
        event.dataTransfer.setData('text/plain', JSON.stringify(payload));
        const sourceCard = event.currentTarget.closest(
            '[data-draggable-calculator-field="true"]'
        );
        if (sourceCard) {
            const rect = sourceCard.getBoundingClientRect();
            const previewNode = sourceCard.cloneNode(true);
            const offsetX = Math.max(
                0,
                Math.min(rect.width - 1, event.clientX - rect.left)
            );
            const offsetY = Math.max(
                0,
                Math.min(rect.height - 1, event.clientY - rect.top)
            );
            previewNode.style.position = 'fixed';
            previewNode.style.top = '-10000px';
            previewNode.style.left = '-10000px';
            previewNode.style.width = `${rect.width}px`;
            previewNode.style.maxWidth = `${rect.width}px`;
            previewNode.style.margin = '0';
            previewNode.style.pointerEvents = 'none';
            previewNode.style.borderRadius = '16px';
            previewNode.style.overflow = 'hidden';
            previewNode.style.boxShadow = '0 18px 36px rgba(15, 23, 42, 0.18)';
            previewNode.style.opacity = '0.98';
            previewNode.style.background = 'white';
            document.body.appendChild(previewNode);
            dragPreviewNodeRef.current = previewNode;
            event.dataTransfer.setDragImage(previewNode, offsetX, offsetY);
        }
        setDraggedField(payload);
    };
    const dragFieldOver = (event, overIndex) => {
        if (!draggedField) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        const targetRect = event.currentTarget.getBoundingClientRect();
        const rawPosition =
            event.clientY - targetRect.top < targetRect.height / 2
                ? 'before'
                : 'after';
        const overPosition = normalizeFieldDropPosition(
            draggedField.fromIndex,
            overIndex,
            rawPosition
        );
        setDraggedField((current) => ({
            ...current,
            overIndex,
            overPosition,
        }));
    };
    const dropField = (event, overIndex) => {
        if (!draggedField) return;
        event.preventDefault();
        const targetRect = event.currentTarget.getBoundingClientRect();
        const rawPosition =
            event.clientY - targetRect.top < targetRect.height / 2
                ? 'before'
                : 'after';
        const overPosition = normalizeFieldDropPosition(
            draggedField.fromIndex,
            overIndex,
            rawPosition
        );
        moveField(
            draggedField.fromIndex,
            overPosition === 'after' ? overIndex + 1 : overIndex
        );
        endFieldDrag();
    };
    const endFieldDrag = () => {
        setDraggedField(null);
        if (dragPreviewNodeRef.current) {
            dragPreviewNodeRef.current.remove();
            dragPreviewNodeRef.current = null;
        }
        suppressFieldDragClickRef.current = true;
        window.setTimeout(() => {
            suppressFieldDragClickRef.current = false;
        }, 0);
    };
    const removeField = (index) => {
        const next = structuredClone(config);
        next.fields.splice(index, 1);
        save(next);
    };
    const addField = () => {
        const next = structuredClone(config);
        const index = next.fields.length;
        next.fields.push({
            id: makeId('New question', index),
            type: 'quantity',
            label: 'New question',
            required: false,
            min: 0,
            step: 1,
        });
        save(next);
        setExpandedField(index);
    };
    const applyTemplate = (id) => {
        setTemplateId(id);
        const template = CALCULATOR_TEMPLATES.find((item) => item.id === id);
        if (template) save(template.config);
    };
    const updateCategory = (categoryIndex, itemIndex, patch) => {
        const next = structuredClone(config);
        next.categories[categoryIndex].items[itemIndex] = {
            ...next.categories[categoryIndex].items[itemIndex],
            ...patch,
        };
        save(next);
    };
    const generate = async () => {
        if (!pricingPrompt.trim() || generating) return;
        setGenerating(true);
        try {
            const response = await API.post(
                `${editor.global.data.company_id}/calculators/generate`,
                { prompt: pricingPrompt, existing_config: config }
            );
            if (!response?.config)
                throw new Error('No calculator configuration returned');
            save(response.config);
            setGenerationSummary(response.summary || []);
            notifications.push({
                title: 'Calculator generated',
                message: 'Review the customer questions and pricing below.',
                variant: 'success',
                ttl: 10,
            });
        } catch (error) {
            notifications.push({
                title: 'Calculator generation failed',
                message: error?.message || 'Try again or use a template.',
                variant: 'error',
                ttl: 20,
                error,
            });
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div className="flex flex-col gap-6 pb-4">
            <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-3 text-red-950">
                <div className="text-xs font-bold uppercase tracking-wide">
                    Beta — testing only
                </div>
                <p className="mt-1 text-xs leading-5">
                    Do not publish this component on a client site yet.
                </p>
            </div>
            <div className="order-1">
                <div className="flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                        <CalculatorIcon
                            className="h-5 w-5"
                            aria-hidden="true"
                        />
                    </span>
                    <h2 className="text-base font-bold text-primary-700">
                        Job pricing calculator
                    </h2>
                </div>
                <p className="mt-1 text-xs leading-5 text-neutral-500">
                    Start with a service template or describe the client’s
                    pricing and let AI build the calculator. You can also tell
                    AI what to add or remove.
                </p>
            </div>

            <div className="order-2 rounded-xl border border-primary-100 bg-primary-50 p-3">
                <label className="block text-xs font-semibold text-primary-700">
                    Start from a template
                </label>
                <select
                    value={templateId}
                    onChange={(event) => applyTemplate(event.target.value)}
                    className="mt-2 w-full rounded-lg border border-primary-200 bg-white px-3 py-2 text-sm text-neutral-800 outline-none focus:border-primary-500"
                >
                    <option value="">Choose an industry or service</option>
                    {CALCULATOR_TEMPLATES.map((template) => (
                        <option key={template.id} value={template.id}>
                            {template.industry} — {template.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className="order-3 rounded-xl border border-secondary-200 bg-secondary-50 p-3">
                <label className="block text-xs font-semibold text-primary-700">
                    Describe pricing for form creation
                </label>
                <p className="mt-1 text-[11px] text-neutral-600">
                    Example: “$150 minimum, $8 per exterior window, 20% extra
                    for second-story work, screens are $3 each.”
                </p>
                <p className="mt-1 text-[11px] text-neutral-600">
                    You can also tell AI what to add or remove from the
                    calculator.
                </p>
                <div className="mt-3">
                    <p className="text-[11px] font-semibold text-primary-700">
                        Suggestions for this template
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {promptSuggestions.map((suggestion) => (
                            <button
                                key={suggestion.label}
                                type="button"
                                onClick={() =>
                                    setPricingPrompt((current) => {
                                        const existing = current.trim();
                                        if (
                                            existing.includes(suggestion.prompt)
                                        )
                                            return current;
                                        return existing
                                            ? `${existing}\n\n${suggestion.prompt}`
                                            : suggestion.prompt;
                                    })
                                }
                                className="inline-flex items-center rounded-full border border-primary-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-primary-700 transition hover:border-primary-400 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
                            >
                                {suggestion.label}
                            </button>
                        ))}
                    </div>
                </div>
                <textarea
                    value={pricingPrompt}
                    onChange={(event) => setPricingPrompt(event.target.value)}
                    placeholder="Paste the client’s pricing notes here…"
                    className="mt-2 min-h-28 w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-neutral-800 outline-none focus:border-primary-500"
                />
                <div className="mt-2 flex justify-end">
                    <button
                        type="button"
                        onClick={generate}
                        disabled={!pricingPrompt.trim() || generating}
                        className="inline-flex items-center gap-2 rounded-md bg-primary-700 px-3 py-2 text-xs font-semibold text-white transition hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {generating && (
                            <ArrowPathIcon
                                className="h-4 w-4 animate-spin"
                                aria-hidden="true"
                            />
                        )}
                        {generating
                            ? 'Building calculator…'
                            : 'Generate calculator'}
                    </button>
                </div>
                {generationSummary.length > 0 && (
                    <ul className="mt-3 list-disc space-y-1 pl-4 text-xs text-neutral-700">
                        {generationSummary.map((item, index) => (
                            <li key={`${item}-${index}`}>{item}</li>
                        ))}
                    </ul>
                )}
            </div>

            <section className="order-last mt-2">
                <div className="text-md mb-3 flex items-center gap-2 font-bold text-primary-700">
                    <SwatchIcon
                        className="h-5 w-5 text-primary-700"
                        aria-hidden="true"
                    />
                    <span>Styling</span>
                </div>
                <div className="grid gap-3">
                    <div>
                        <label className="block text-xs font-semibold text-primary-700">
                            Color scheme
                        </label>
                        <div className="mt-1">
                            <ColorSchemeSelect
                                value={
                                    componentProps?.ColorScheme ||
                                    'dark-gradient'
                                }
                                onChange={(value) =>
                                    updateComponentProp('ColorScheme', value)
                                }
                                groups={COMPONENT_COLOR_SCHEME_GROUPS.filter(
                                    (group) => group.label !== 'Box Effects'
                                )}
                                swatchFn={componentSchemeToSwatch}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                            Alignment
                        </label>
                        <div className="mt-1">
                            <AlignmentButtons
                                value={componentProps?.Alignment || 'left'}
                                allowed={['left', 'right']}
                                hideUnsupported
                                onChange={(value) =>
                                    updateComponentProp('Alignment', value)
                                }
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                            Question display
                        </label>
                        <div className="mt-1">
                            <QuestionDisplayButtons
                                value={componentProps?.QuestionDisplay || 'all'}
                                onChange={(value) =>
                                    updateComponentProp(
                                        'QuestionDisplay',
                                        value
                                    )
                                }
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                            Vertical alignment
                        </label>
                        <div className="mt-1">
                            <VerticalAlignmentButtons
                                value={
                                    componentProps?.VerticalAlignment ||
                                    'center'
                                }
                                onChange={(value) => {
                                    updateComponentProp(
                                        'VerticalAlignment',
                                        value
                                    );
                                    if (
                                        value === 'bottom' &&
                                        componentProps?.StickyMedia !== false
                                    ) {
                                        updateComponentProp(
                                            'StickyMedia',
                                            false
                                        );
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <div>
                        <MediaPill
                            title="Background image"
                            value={componentProps?.BackgroundImage}
                            expanded={openMediaPill === 'background'}
                            onToggle={() =>
                                setOpenMediaPill((current) =>
                                    current === 'background'
                                        ? null
                                        : 'background'
                                )
                            }
                            onSelect={(value) =>
                                updateComponentProp('BackgroundImage', value)
                            }
                            onRemove={() =>
                                updateComponentProp('BackgroundImage', '')
                            }
                        >
                            <div>
                                <label className="block text-xs font-semibold leading-5 text-primary-700">
                                    Background position
                                </label>
                                <div className="mt-1">
                                    <AlignmentButtons
                                        value={
                                            componentProps?.BackgroundImagePosition ||
                                            'center'
                                        }
                                        allowed={['left', 'center', 'right']}
                                        hideUnsupported
                                        onChange={(value) =>
                                            updateComponentProp(
                                                'BackgroundImagePosition',
                                                value
                                            )
                                        }
                                    />
                                </div>
                            </div>
                        </MediaPill>
                    </div>
                    <AnimationControls
                        component_props={componentProps}
                        file={file}
                        componentName="JobPricingCalculator"
                        onChange={onChange}
                    />
                    <div>
                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                            Vertical Spacing
                        </label>
                        <div className="mt-1">
                            <VerticalSpacingRow
                                value={
                                    componentProps?.VerticalSpacing || 'medium'
                                }
                                onChange={(value) =>
                                    updateComponentProp(
                                        'VerticalSpacing',
                                        value
                                    )
                                }
                            />
                        </div>
                    </div>
                    <div className="flex items-center justify-between rounded-lg bg-neutral-100 px-3 py-2">
                        <div className="pr-3">
                            <div className="text-xs font-semibold leading-5 text-primary-700">
                                Sticky results panel
                            </div>
                            <div className="text-[11px] leading-4 text-neutral-500">
                                {componentProps?.VerticalAlignment === 'bottom'
                                    ? 'Unavailable with bottom alignment.'
                                    : 'Pins Results while the questions scroll.'}
                            </div>
                        </div>
                        <Toggle
                            checked={
                                componentProps?.VerticalAlignment !==
                                    'bottom' &&
                                componentProps?.StickyMedia !== false
                            }
                            onChange={(value) =>
                                updateComponentProp('StickyMedia', value)
                            }
                            ariaLabel="Toggle sticky results panel"
                            disabled={
                                componentProps?.VerticalAlignment === 'bottom'
                            }
                        />
                    </div>
                </div>
            </section>

            <EditorPill
                id="pricing"
                title="Pricing"
                description="Prices and service rows"
                tone="secondary"
                order={pillOrderValue('pricing')}
                expanded={Boolean(openPills.pricing)}
                onToggle={() => togglePill('pricing')}
                onDragStart={setDraggedPill}
                onDrop={movePill}
            >
                <p className="text-[11px] text-neutral-500">
                    Set the actual amounts here. AI creates these rows, but you
                    can always edit every price manually.
                </p>
                <label className="mt-3 block text-xs font-semibold text-primary-700">
                    Minimum job price
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={config.minimumJobPrice ?? 0}
                        onChange={(event) =>
                            update(
                                'minimumJobPrice',
                                Number(event.target.value)
                            )
                        }
                        className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal text-neutral-800"
                    />
                </label>
                <div className="mt-4 space-y-4">
                    {(config.categories || []).map(
                        (category, categoryIndex) => (
                            <div
                                key={category.id || categoryIndex}
                                className="rounded-lg border border-neutral-200 bg-neutral-100 p-3"
                            >
                                <div className="text-sm font-semibold text-neutral-800">
                                    {category.name || 'Service pricing'}
                                </div>
                                {(category.items || []).map(
                                    (item, itemIndex) => (
                                        <div
                                            key={`${item.fieldId}-${itemIndex}`}
                                            className="mt-3 rounded-lg bg-white p-2"
                                        >
                                            <div className="text-xs font-semibold text-primary-700">
                                                {config.fields.find(
                                                    (field) =>
                                                        field.id ===
                                                        item.fieldId
                                                )?.label ||
                                                    item.fieldId ||
                                                    'Pricing item'}
                                            </div>
                                            {item.options?.length ? (
                                                <div className="mt-2 space-y-2">
                                                    {item.options.map(
                                                        (
                                                            option,
                                                            optionIndex
                                                        ) => (
                                                            <label
                                                                key={
                                                                    option.value ||
                                                                    optionIndex
                                                                }
                                                                className="grid grid-cols-[1fr_100px] items-center gap-2 text-xs text-neutral-700"
                                                            >
                                                                <span>
                                                                    {option.label ||
                                                                        option.value}
                                                                </span>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.01"
                                                                    value={
                                                                        option.price ??
                                                                        0
                                                                    }
                                                                    onChange={(
                                                                        event
                                                                    ) => {
                                                                        const options =
                                                                            structuredClone(
                                                                                item.options
                                                                            );
                                                                        options[
                                                                            optionIndex
                                                                        ].price =
                                                                            Number(
                                                                                event
                                                                                    .target
                                                                                    .value
                                                                            );
                                                                        updateCategory(
                                                                            categoryIndex,
                                                                            itemIndex,
                                                                            {
                                                                                options,
                                                                            }
                                                                        );
                                                                    }}
                                                                    className="rounded-md border border-neutral-200 px-2 py-1.5 text-sm"
                                                                />
                                                            </label>
                                                        )
                                                    )}
                                                </div>
                                            ) : (
                                                <label className="mt-2 grid grid-cols-[1fr_100px] items-center gap-2 text-xs text-neutral-700">
                                                    <span>Price</span>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={item.price ?? 0}
                                                        onChange={(event) =>
                                                            updateCategory(
                                                                categoryIndex,
                                                                itemIndex,
                                                                {
                                                                    price: Number(
                                                                        event
                                                                            .target
                                                                            .value
                                                                    ),
                                                                }
                                                            )
                                                        }
                                                        className="rounded-md border border-neutral-200 px-2 py-1.5 text-sm"
                                                    />
                                                </label>
                                            )}
                                        </div>
                                    )
                                )}
                            </div>
                        )
                    )}
                </div>
            </EditorPill>

            <section className="order-4">
                <label className="mb-3 block text-xs font-semibold text-primary-700">
                    Anchor
                    <input
                        value={componentProps?.Anchor || ''}
                        onChange={(event) =>
                            updateComponentProp('Anchor', event.target.value)
                        }
                        placeholder="job-pricing-calculator"
                        className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal text-neutral-800 outline-none focus:border-primary-500"
                    />
                </label>
                <div className="grid gap-3">
                    {[
                        ['content.tagline', 'Tagline'],
                        ['content.heading', 'Heading'],
                        ['content.description', 'Description'],
                    ].map(([path, label]) => (
                        <div key={path}>
                            <label className="block text-xs font-semibold text-primary-700">
                                {label}
                            </label>
                            <div className="mt-1">
                                <MDEditor
                                    value={
                                        path
                                            .split('.')
                                            .reduce(
                                                (value, key) => value?.[key],
                                                config
                                            ) || ''
                                    }
                                    fieldKey={`CalculatorConfig.${path}`}
                                    componentName="JobPricingCalculator"
                                    onChange={(value) => update(path, value)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <EditorPill
                id="results"
                title="Results"
                description="Estimate Card"
                order={pillOrderValue('results')}
                expanded={Boolean(openPills.results)}
                onToggle={() => togglePill('results')}
                onDragStart={setDraggedPill}
                onDrop={movePill}
                tone="primary"
            >
                <div className="grid gap-3">
                    <div>
                        <label className="block text-xs font-semibold text-primary-700">
                            Color scheme
                        </label>
                        <div className="mt-1">
                            <ColorSchemeSelect
                                value={
                                    componentProps?.ResultColorScheme || 'white'
                                }
                                onChange={(value) =>
                                    updateComponentProp(
                                        'ResultColorScheme',
                                        value
                                    )
                                }
                                groups={COMPONENT_COLOR_SCHEME_GROUPS.filter(
                                    (group) => group.label !== 'Box Effects'
                                )}
                                swatchFn={componentSchemeToSwatch}
                            />
                        </div>
                    </div>
                    <div>
                        <MediaPill
                            title="Results image"
                            value={componentProps?.ResultImage}
                            expanded={openMediaPill === 'results'}
                            onToggle={() =>
                                setOpenMediaPill((current) =>
                                    current === 'results' ? null : 'results'
                                )
                            }
                            onSelect={(value) =>
                                updateComponentProp('ResultImage', value)
                            }
                            onRemove={() =>
                                updateComponentProp('ResultImage', '')
                            }
                        >
                            <p className="text-[11px] leading-4 text-neutral-500">
                                The image fills the open space in the results
                                card.
                            </p>
                        </MediaPill>
                    </div>
                    {[
                        ['content.resultLabel', 'Result label'],
                        ['content.disclaimer', 'Estimate disclaimer'],
                        ['content.customQuoteMessage', 'Custom quote message'],
                    ].map(([path, label]) => (
                        <label
                            key={path}
                            className="block text-xs font-semibold text-primary-700"
                        >
                            {label}
                            <input
                                value={
                                    path
                                        .split('.')
                                        .reduce(
                                            (value, key) => value?.[key],
                                            config
                                        ) || ''
                                }
                                onChange={(event) =>
                                    update(path, event.target.value)
                                }
                                className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal text-neutral-800 outline-none focus:border-primary-500"
                            />
                        </label>
                    ))}
                    <label className="block text-xs font-semibold text-primary-700">
                        Result mode
                        <select
                            value={config.result?.mode || 'exact'}
                            onChange={(event) =>
                                update('result.mode', event.target.value)
                            }
                            className="mt-1 w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm font-normal text-neutral-800 outline-none focus:border-primary-500"
                        >
                            <option value="exact">Exact price</option>
                            <option value="range">Estimated range</option>
                            <option value="starting">Starting price</option>
                            <option value="custom-quote">
                                Request a custom quote
                            </option>
                        </select>
                    </label>
                </div>
            </EditorPill>

            <EditorPill
                id="ctas"
                title="CTA buttons"
                description={
                    config.actions?.primaryText || 'Configure result actions'
                }
                order={pillOrderValue('ctas')}
                expanded={Boolean(openPills.ctas)}
                onToggle={() => togglePill('ctas')}
                onDragStart={setDraggedPill}
                onDrop={movePill}
                tone="cta"
            >
                <div className="space-y-3">
                    <div className="rounded-xl bg-primary-50 p-3">
                        <ActionControls
                            label="CTA"
                            text={config.actions?.primaryText}
                            link={config.actions?.primaryLink}
                            icon={config.actions?.primaryIcon}
                            iconVisible={config.actions?.primaryIconVisible}
                            openInNewTab={config.actions?.primaryOpenInNewTab}
                            onTextChange={(value) =>
                                update('actions.primaryText', value)
                            }
                            onLinkChange={(value) =>
                                update('actions.primaryLink', value)
                            }
                            onIconChange={(value) =>
                                update('actions.primaryIcon', value)
                            }
                            onIconVisibleChange={(value) =>
                                update('actions.primaryIconVisible', value)
                            }
                            onOpenInNewTabChange={(value) =>
                                update('actions.primaryOpenInNewTab', value)
                            }
                        />
                        <div className="mt-3 space-y-4 rounded-lg bg-white/70 px-2.5 py-2">
                            <p className="text-[11px] leading-4 text-neutral-600">
                                When the CTA link is empty, the estimate card
                                expands into this form and sends a NiceJob
                                webform event.
                            </p>
                            <div className="grid gap-3 border-t border-primary-100 pt-3">
                                {[
                                    ['leadCapture.eyebrow', 'Tagline'],
                                    ['leadCapture.title', 'Form title'],
                                    [
                                        'leadCapture.submitText',
                                        'Submit button text',
                                    ],
                                    [
                                        'leadCapture.successTitle',
                                        'Success title',
                                    ],
                                ].map(([path, label]) => (
                                    <label
                                        key={path}
                                        className="block text-xs font-semibold text-primary-700"
                                    >
                                        {label}
                                        <input
                                            value={
                                                path
                                                    .split('.')
                                                    .reduce(
                                                        (value, key) =>
                                                            value?.[key],
                                                        config
                                                    ) ??
                                                LEAD_COPY_DEFAULTS[path]
                                            }
                                            onChange={(event) =>
                                                update(path, event.target.value)
                                            }
                                            className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal text-neutral-800 outline-none focus:border-primary-500"
                                        />
                                    </label>
                                ))}
                                {[
                                    [
                                        'leadCapture.description',
                                        'Form description',
                                    ],
                                    [
                                        'leadCapture.successMessage',
                                        'Success message',
                                    ],
                                ].map(([path, label]) => (
                                    <label
                                        key={path}
                                        className="block text-xs font-semibold text-primary-700"
                                    >
                                        {label}
                                        <textarea
                                            value={
                                                path
                                                    .split('.')
                                                    .reduce(
                                                        (value, key) =>
                                                            value?.[key],
                                                        config
                                                    ) ??
                                                LEAD_COPY_DEFAULTS[path]
                                            }
                                            onChange={(event) =>
                                                update(path, event.target.value)
                                            }
                                            className="mt-1 min-h-20 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal leading-5 text-neutral-800 outline-none focus:border-primary-500"
                                        />
                                    </label>
                                ))}
                            </div>
                            <div className="space-y-2 border-t border-primary-100 pt-3">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <p className="text-xs font-semibold text-primary-700">
                                            Form fields
                                        </p>
                                        <p className="mt-0.5 text-[11px] text-neutral-500">
                                            Email is always required by NiceJob.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={addLeadField}
                                        className="inline-flex items-center gap-1 rounded-lg border border-primary-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-primary-700 hover:bg-primary-50"
                                    >
                                        <PlusIcon className="h-3.5 w-3.5" /> Add
                                        field
                                    </button>
                                </div>
                                {leadFields.map((field, index) => {
                                    const expanded =
                                        expandedLeadField === index;
                                    const isDragged =
                                        draggedLeadField?.fromIndex === index;
                                    const isDropTarget =
                                        draggedLeadField?.fromIndex !== index &&
                                        draggedLeadField?.overIndex === index;
                                    const showDropPlaceholderBefore =
                                        isDropTarget &&
                                        draggedLeadField?.overPosition ===
                                            'before' &&
                                        !isNoOpFieldDrop(
                                            draggedLeadField.fromIndex,
                                            index,
                                            'before'
                                        );
                                    const showDropPlaceholderAfter =
                                        isDropTarget &&
                                        draggedLeadField?.overPosition ===
                                            'after' &&
                                        !isNoOpFieldDrop(
                                            draggedLeadField.fromIndex,
                                            index,
                                            'after'
                                        );
                                    return (
                                        <div
                                            key={field.id}
                                            onDragOver={(event) =>
                                                dragLeadFieldOver(event, index)
                                            }
                                            onDrop={(event) =>
                                                dropLeadField(event, index)
                                            }
                                        >
                                            {showDropPlaceholderBefore && (
                                                <div
                                                    className="bg-primary-50/80 my-3 h-14 rounded-xl border-2 border-dashed border-primary-300"
                                                    aria-hidden="true"
                                                />
                                            )}
                                            <div
                                                data-draggable-lead-field="true"
                                                className={`overflow-hidden rounded-xl border border-primary-200 bg-primary-50 shadow-sm transition-all ${
                                                    isDragged
                                                        ? 'scale-[0.985] opacity-70'
                                                        : ''
                                                }`}
                                            >
                                                <div
                                                    className={`flex items-center gap-2 px-3 py-2 ${
                                                        expanded
                                                            ? 'border-b border-primary-200'
                                                            : ''
                                                    }`}
                                                >
                                                    <div
                                                        role="button"
                                                        tabIndex={0}
                                                        draggable
                                                        onDragStart={(event) =>
                                                            startLeadFieldDrag(
                                                                event,
                                                                index
                                                            )
                                                        }
                                                        onDragEnd={
                                                            endLeadFieldDrag
                                                        }
                                                        onClick={() => {
                                                            if (
                                                                suppressLeadFieldDragClickRef.current
                                                            )
                                                                return;
                                                            setExpandedLeadField(
                                                                expanded
                                                                    ? null
                                                                    : index
                                                            );
                                                        }}
                                                        onKeyDown={(event) => {
                                                            if (
                                                                event.key ===
                                                                    'Enter' ||
                                                                event.key ===
                                                                    ' '
                                                            ) {
                                                                event.preventDefault();
                                                                setExpandedLeadField(
                                                                    expanded
                                                                        ? null
                                                                        : index
                                                                );
                                                            }
                                                        }}
                                                        className="hover:bg-primary-100/70 flex min-w-0 flex-1 cursor-grab items-center gap-3 rounded-lg p-1 text-left transition-colors ease-in focus-visible:outline-none active:cursor-grabbing"
                                                    >
                                                        <span className="min-w-0">
                                                            <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700">
                                                                {field.id ===
                                                                'email'
                                                                    ? 'Required email field'
                                                                    : `Contact field ${
                                                                          index +
                                                                          1
                                                                      }`}
                                                            </span>
                                                            <span className="mt-1 block truncate text-sm font-semibold text-neutral-900">
                                                                {field.label ||
                                                                    'Untitled field'}
                                                            </span>
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            type="button"
                                                            aria-label="Move contact field up"
                                                            disabled={
                                                                index === 0
                                                            }
                                                            onClick={() =>
                                                                moveLeadField(
                                                                    index,
                                                                    index - 1
                                                                )
                                                            }
                                                            className="hover:bg-primary-100/70 inline-flex h-6 w-6 items-center justify-center rounded-full text-primary-700 transition-colors disabled:opacity-30"
                                                        >
                                                            <ChevronUpIcon className="h-3 w-3" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            aria-label="Move contact field down"
                                                            disabled={
                                                                index ===
                                                                leadFields.length -
                                                                    1
                                                            }
                                                            onClick={() =>
                                                                moveLeadField(
                                                                    index,
                                                                    index + 2
                                                                )
                                                            }
                                                            className="hover:bg-primary-100/70 inline-flex h-6 w-6 items-center justify-center rounded-full text-primary-700 transition-colors disabled:opacity-30"
                                                        >
                                                            <ChevronDownIcon className="h-3 w-3" />
                                                        </button>
                                                        {field.id !==
                                                            'email' && (
                                                            <button
                                                                type="button"
                                                                aria-label={`Remove ${field.label}`}
                                                                onClick={() =>
                                                                    removeLeadField(
                                                                        index
                                                                    )
                                                                }
                                                                className="inline-flex h-6 w-6 items-center justify-center rounded-full text-primary-700 transition-colors hover:bg-red-50 hover:text-red-600"
                                                            >
                                                                <TrashIcon className="h-3 w-3" />
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            aria-label={
                                                                expanded
                                                                    ? 'Collapse contact field'
                                                                    : 'Expand contact field'
                                                            }
                                                            onClick={() =>
                                                                setExpandedLeadField(
                                                                    expanded
                                                                        ? null
                                                                        : index
                                                                )
                                                            }
                                                            className="hover:bg-primary-100/70 inline-flex h-6 w-6 items-center justify-center rounded-full text-primary-700 transition-colors"
                                                        >
                                                            <ChevronDownIcon
                                                                className={`h-3 w-3 transition-transform duration-200 ${
                                                                    expanded
                                                                        ? 'rotate-180'
                                                                        : ''
                                                                }`}
                                                            />
                                                        </button>
                                                    </div>
                                                </div>
                                                {expanded && (
                                                    <div className="grid gap-3 bg-white px-3 pb-3 pt-2">
                                                        <label className="text-xs font-semibold text-primary-700">
                                                            Label
                                                            <input
                                                                value={
                                                                    field.label
                                                                }
                                                                onChange={(
                                                                    event
                                                                ) =>
                                                                    updateLeadField(
                                                                        index,
                                                                        {
                                                                            label: event
                                                                                .target
                                                                                .value,
                                                                        }
                                                                    )
                                                                }
                                                                className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal text-neutral-800"
                                                            />
                                                        </label>
                                                        <label className="text-xs font-semibold text-primary-700">
                                                            Placeholder
                                                            <input
                                                                value={
                                                                    field.placeholder
                                                                }
                                                                onChange={(
                                                                    event
                                                                ) =>
                                                                    updateLeadField(
                                                                        index,
                                                                        {
                                                                            placeholder:
                                                                                event
                                                                                    .target
                                                                                    .value,
                                                                        }
                                                                    )
                                                                }
                                                                className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal text-neutral-800"
                                                            />
                                                        </label>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <label className="text-xs font-semibold text-primary-700">
                                                                Type
                                                                <select
                                                                    value={
                                                                        field.type
                                                                    }
                                                                    disabled={
                                                                        field.id ===
                                                                        'email'
                                                                    }
                                                                    onChange={(
                                                                        event
                                                                    ) =>
                                                                        updateLeadField(
                                                                            index,
                                                                            {
                                                                                type: event
                                                                                    .target
                                                                                    .value,
                                                                            }
                                                                        )
                                                                    }
                                                                    className="mt-1 w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm font-normal text-neutral-800 disabled:bg-neutral-100"
                                                                >
                                                                    {LEAD_FIELD_TYPES.map(
                                                                        ([
                                                                            value,
                                                                            label,
                                                                        ]) => (
                                                                            <option
                                                                                key={
                                                                                    value
                                                                                }
                                                                                value={
                                                                                    value
                                                                                }
                                                                            >
                                                                                {
                                                                                    label
                                                                                }
                                                                            </option>
                                                                        )
                                                                    )}
                                                                </select>
                                                            </label>
                                                            <label className="flex items-end gap-2 pb-2 text-xs font-semibold text-primary-700">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={
                                                                        field.required
                                                                    }
                                                                    disabled={
                                                                        field.id ===
                                                                        'email'
                                                                    }
                                                                    onChange={(
                                                                        event
                                                                    ) =>
                                                                        updateLeadField(
                                                                            index,
                                                                            {
                                                                                required:
                                                                                    event
                                                                                        .target
                                                                                        .checked,
                                                                            }
                                                                        )
                                                                    }
                                                                />
                                                                Required
                                                            </label>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {showDropPlaceholderAfter && (
                                                <div
                                                    className="bg-primary-50/80 my-3 h-14 rounded-xl border-2 border-dashed border-primary-300"
                                                    aria-hidden="true"
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="space-y-3 border-t border-primary-100 pt-3">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <p className="text-xs font-semibold text-primary-700">
                                            Project photo upload
                                        </p>
                                        <p className="mt-0.5 text-[11px] leading-4 text-neutral-600">
                                            Sends uploaded photo links with the
                                            NiceJob lead.
                                        </p>
                                    </div>
                                    <Toggle
                                        checked={Boolean(
                                            config.leadCapture?.photoUpload
                                                ?.enabled
                                        )}
                                        onChange={(value) =>
                                            update(
                                                'leadCapture.photoUpload.enabled',
                                                value
                                            )
                                        }
                                        ariaLabel="Allow project photo uploads"
                                    />
                                </div>
                                {config.leadCapture?.photoUpload?.enabled && (
                                    <>
                                        <label className="block text-xs font-semibold text-primary-700">
                                            Field label
                                            <input
                                                value={
                                                    config.leadCapture
                                                        ?.photoUpload?.label ||
                                                    'Upload project photos'
                                                }
                                                onChange={(event) =>
                                                    update(
                                                        'leadCapture.photoUpload.label',
                                                        event.target.value
                                                    )
                                                }
                                                className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal text-neutral-800"
                                            />
                                        </label>
                                        <label className="block text-xs font-semibold text-primary-700">
                                            Upload instructions
                                            <input
                                                value={
                                                    config.leadCapture
                                                        ?.photoUpload
                                                        ?.helperText ||
                                                    'Add up to 5 photos. JPG, PNG, or WebP.'
                                                }
                                                onChange={(event) =>
                                                    update(
                                                        'leadCapture.photoUpload.helperText',
                                                        event.target.value
                                                    )
                                                }
                                                className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal text-neutral-800"
                                            />
                                        </label>
                                        <label className="flex items-center justify-between gap-3 text-xs font-semibold text-primary-700">
                                            Require at least one photo
                                            <Toggle
                                                checked={Boolean(
                                                    config.leadCapture
                                                        ?.photoUpload?.required
                                                )}
                                                onChange={(value) =>
                                                    update(
                                                        'leadCapture.photoUpload.required',
                                                        value
                                                    )
                                                }
                                                ariaLabel="Require a project photo"
                                            />
                                        </label>
                                    </>
                                )}
                            </div>
                            <div className="space-y-3 border-t border-primary-100 pt-3">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <p className="text-xs font-semibold text-primary-700">
                                            Terms and conditions
                                        </p>
                                        <p className="mt-0.5 text-[11px] leading-4 text-neutral-600">
                                            Adds a consent checkbox to the form.
                                        </p>
                                    </div>
                                    <Toggle
                                        checked={Boolean(
                                            config.leadCapture?.terms?.enabled
                                        )}
                                        onChange={(value) =>
                                            update(
                                                'leadCapture.terms.enabled',
                                                value
                                            )
                                        }
                                        ariaLabel="Show terms and conditions"
                                    />
                                </div>
                                {config.leadCapture?.terms?.enabled && (
                                    <>
                                        <label className="flex items-center justify-between gap-3 text-xs font-semibold text-primary-700">
                                            Require acceptance
                                            <Toggle
                                                checked={
                                                    config.leadCapture?.terms
                                                        ?.required !== false
                                                }
                                                onChange={(value) =>
                                                    update(
                                                        'leadCapture.terms.required',
                                                        value
                                                    )
                                                }
                                                ariaLabel="Require terms acceptance"
                                            />
                                        </label>
                                        <label className="block text-xs font-semibold text-primary-700">
                                            Consent message
                                            <textarea
                                                value={
                                                    config.leadCapture?.terms
                                                        ?.message || ''
                                                }
                                                onChange={(event) =>
                                                    update(
                                                        'leadCapture.terms.message',
                                                        event.target.value
                                                    )
                                                }
                                                className="mt-1 min-h-24 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal leading-5 text-neutral-800 outline-none focus:border-primary-500"
                                            />
                                        </label>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="rounded-xl bg-secondary-50 p-3">
                        <ActionControls
                            label="Secondary Button"
                            text={config.actions?.secondaryText}
                            link={config.actions?.secondaryLink}
                            icon={config.actions?.secondaryIcon}
                            iconVisible={config.actions?.secondaryIconVisible}
                            openInNewTab={config.actions?.secondaryOpenInNewTab}
                            onTextChange={(value) =>
                                update('actions.secondaryText', value)
                            }
                            onLinkChange={(value) =>
                                update('actions.secondaryLink', value)
                            }
                            onIconChange={(value) =>
                                update('actions.secondaryIcon', value)
                            }
                            onIconVisibleChange={(value) =>
                                update('actions.secondaryIconVisible', value)
                            }
                            onOpenInNewTabChange={(value) =>
                                update('actions.secondaryOpenInNewTab', value)
                            }
                        />
                    </div>
                </div>
            </EditorPill>

            <section className="order-5">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                            Customer questions
                        </h3>
                        <p className="mt-1 text-[11px] text-neutral-500">
                            Drag or use arrows to change the order visitors see.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={addField}
                        className="inline-flex items-center gap-1 rounded-md bg-primary-700 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-primary-800"
                    >
                        <PlusIcon className="h-4 w-4" /> Add field
                    </button>
                </div>
                <div className="mt-3 space-y-2">
                    {config.fields.map((field, index) => {
                        const expanded = expandedField === index;
                        const isDragged = draggedField?.fromIndex === index;
                        const isDropTarget =
                            draggedField?.fromIndex !== index &&
                            draggedField?.overIndex === index;
                        const showDropPlaceholderBefore =
                            isDropTarget &&
                            draggedField?.overPosition === 'before' &&
                            !isNoOpFieldDrop(
                                draggedField.fromIndex,
                                index,
                                'before'
                            );
                        const showDropPlaceholderAfter =
                            isDropTarget &&
                            draggedField?.overPosition === 'after' &&
                            !isNoOpFieldDrop(
                                draggedField.fromIndex,
                                index,
                                'after'
                            );
                        return (
                            <div
                                key={field.id || index}
                                onDragOver={(event) =>
                                    dragFieldOver(event, index)
                                }
                                onDrop={(event) => dropField(event, index)}
                            >
                                {showDropPlaceholderBefore && (
                                    <div
                                        className="bg-primary-50/80 my-3 h-16 rounded-xl border-2 border-dashed border-primary-300"
                                        aria-hidden="true"
                                    />
                                )}
                                <div
                                    data-draggable-calculator-field="true"
                                    className={`overflow-hidden rounded-xl border border-primary-200 bg-primary-50 shadow-sm transition-all ${
                                        isDragged
                                            ? 'scale-[0.985] opacity-70'
                                            : ''
                                    }`}
                                >
                                    <div
                                        className={`flex items-center gap-2 px-3 py-2 ${
                                            expanded
                                                ? 'border-b border-primary-200'
                                                : ''
                                        }`}
                                    >
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            draggable
                                            onDragStart={(event) =>
                                                startFieldDrag(event, index)
                                            }
                                            onDragEnd={endFieldDrag}
                                            onClick={() => {
                                                if (
                                                    suppressFieldDragClickRef.current
                                                )
                                                    return;
                                                setExpandedField(
                                                    expanded ? null : index
                                                );
                                            }}
                                            onKeyDown={(event) => {
                                                if (
                                                    event.key === 'Enter' ||
                                                    event.key === ' '
                                                ) {
                                                    event.preventDefault();
                                                    setExpandedField(
                                                        expanded ? null : index
                                                    );
                                                }
                                            }}
                                            className="hover:bg-primary-100/70 flex min-w-0 flex-1 cursor-grab items-center gap-3 rounded-lg p-1 text-left transition-colors ease-in focus-visible:outline-none active:cursor-grabbing"
                                        >
                                            <span className="min-w-0">
                                                <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700">
                                                    Question {index + 1}
                                                </span>
                                                <span className="mt-1 block truncate text-sm font-semibold text-neutral-900">
                                                    {field.label ||
                                                        'Untitled question'}
                                                </span>
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                type="button"
                                                aria-label="Move field up"
                                                disabled={index === 0}
                                                onClick={() =>
                                                    moveField(index, index - 1)
                                                }
                                                className="hover:bg-primary-100/70 inline-flex h-6 w-6 items-center justify-center rounded-full text-primary-700 transition-colors disabled:opacity-30"
                                            >
                                                <ChevronUpIcon className="h-3 w-3" />
                                            </button>
                                            <button
                                                type="button"
                                                aria-label="Move field down"
                                                disabled={
                                                    index ===
                                                    config.fields.length - 1
                                                }
                                                onClick={() =>
                                                    moveField(index, index + 2)
                                                }
                                                className="hover:bg-primary-100/70 inline-flex h-6 w-6 items-center justify-center rounded-full text-primary-700 transition-colors disabled:opacity-30"
                                            >
                                                <ChevronDownIcon className="h-3 w-3" />
                                            </button>
                                            <button
                                                type="button"
                                                aria-label="Remove field"
                                                onClick={() =>
                                                    removeField(index)
                                                }
                                                className="inline-flex h-6 w-6 items-center justify-center rounded-full text-primary-700 transition-colors hover:bg-red-50 hover:text-red-600"
                                            >
                                                <TrashIcon className="h-3 w-3" />
                                            </button>
                                            <button
                                                type="button"
                                                aria-label={
                                                    expanded
                                                        ? 'Collapse question'
                                                        : 'Expand question'
                                                }
                                                onClick={() =>
                                                    setExpandedField(
                                                        expanded ? null : index
                                                    )
                                                }
                                                className="hover:bg-primary-100/70 inline-flex h-6 w-6 items-center justify-center rounded-full text-primary-700 transition-colors"
                                            >
                                                <ChevronDownIcon
                                                    className={`h-3 w-3 transition-transform duration-200 ${
                                                        expanded
                                                            ? 'rotate-180'
                                                            : ''
                                                    }`}
                                                />
                                            </button>
                                        </div>
                                    </div>
                                    {expanded && (
                                        <div className="grid gap-3 bg-white px-3 pb-3 pt-2">
                                            <label className="text-xs font-semibold text-primary-700">
                                                Question label
                                                <input
                                                    value={field.label || ''}
                                                    onChange={(event) =>
                                                        updateField(index, {
                                                            label: event.target
                                                                .value,
                                                        })
                                                    }
                                                    className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal text-neutral-800"
                                                />
                                            </label>
                                            <label className="text-xs font-semibold text-primary-700">
                                                Field type
                                                <select
                                                    value={
                                                        field.type || 'quantity'
                                                    }
                                                    onChange={(event) =>
                                                        updateField(index, {
                                                            type: event.target
                                                                .value,
                                                        })
                                                    }
                                                    className="mt-1 w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm font-normal text-neutral-800"
                                                >
                                                    {FIELD_TYPES.map(
                                                        ([value, label]) => (
                                                            <option
                                                                key={value}
                                                                value={value}
                                                            >
                                                                {label}
                                                            </option>
                                                        )
                                                    )}
                                                </select>
                                            </label>
                                            <label className="flex items-center gap-2 text-xs font-semibold text-primary-700">
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(
                                                        field.required
                                                    )}
                                                    onChange={(event) =>
                                                        updateField(index, {
                                                            required:
                                                                event.target
                                                                    .checked,
                                                        })
                                                    }
                                                />{' '}
                                                Required
                                            </label>
                                            {optionFields.has(field.type) && (
                                                <label className="text-xs font-semibold text-primary-700">
                                                    Options (one per line)
                                                    <textarea
                                                        value={(
                                                            field.options || []
                                                        )
                                                            .map(
                                                                (option) =>
                                                                    option.label
                                                            )
                                                            .join('\n')}
                                                        onChange={(event) =>
                                                            updateField(index, {
                                                                options:
                                                                    event.target.value
                                                                        .split(
                                                                            '\n'
                                                                        )
                                                                        .filter(
                                                                            Boolean
                                                                        )
                                                                        .map(
                                                                            (
                                                                                label
                                                                            ) => ({
                                                                                label,
                                                                                value: label
                                                                                    .toLowerCase()
                                                                                    .replace(
                                                                                        /[^a-z0-9]+/g,
                                                                                        '-'
                                                                                    ),
                                                                            })
                                                                        ),
                                                            })
                                                        }
                                                        className="mt-1 min-h-20 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal text-neutral-800"
                                                    />
                                                </label>
                                            )}
                                            <ConditionGroupEditor
                                                group={
                                                    field.visibility || {
                                                        logic: 'and',
                                                        conditions: [],
                                                    }
                                                }
                                                fields={config.fields.slice(
                                                    0,
                                                    index
                                                )}
                                                onChange={(visibility) =>
                                                    updateFieldVisibility(
                                                        index,
                                                        visibility
                                                    )
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                                {showDropPlaceholderAfter && (
                                    <div
                                        className="bg-primary-50/80 my-3 h-16 rounded-xl border-2 border-dashed border-primary-300"
                                        aria-hidden="true"
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>

            {errors.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                    <strong>Fix before publishing:</strong>
                    <ul className="mt-1 list-disc pl-4">
                        {errors.map((error) => (
                            <li key={error}>{error}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
